import { FileTextIcon, XIcon } from 'lucide-react';
import type { ChatRagScopeDocumentDTO } from '@shared/schemas/chat';

type ChatRagScopeBarProps = {
  disabled?: boolean;
  documents: readonly ChatRagScopeDocumentDTO[];
  onChange: (documents: ChatRagScopeDocumentDTO[]) => void;
};

/**
 * Documents the conversation search is limited to, above the composer. Removing
 * the last one (or « Tout le corpus ») widens the next search to the whole corpus.
 */
export function ChatRagScopeBar({ disabled = false, documents, onChange }: ChatRagScopeBarProps) {
  if (documents.length === 0) {
    return null;
  }

  return (
    <div
      aria-label="Périmètre de la recherche documentaire"
      className="mb-2 flex flex-wrap items-center gap-1.5 px-1 text-xs"
      data-testid="rag-scope-bar"
      role="group"
    >
      <span className="text-muted-foreground">Recherche limitée à</span>
      {documents.map((document) => (
        <span
          key={document.id}
          className="inline-flex max-w-60 items-center gap-1 rounded-full border border-border/70 bg-muted/40 py-0.5 pl-2 pr-0.5 text-foreground"
        >
          <FileTextIcon aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
          <span className="truncate" title={document.name}>{document.name}</span>
          <button
            aria-label={`Retirer ${document.name} du périmètre`}
            className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            disabled={disabled}
            onClick={() => onChange(documents.filter((candidate) => candidate.id !== document.id))}
            type="button"
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
      <button
        className="rounded px-1 text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        disabled={disabled}
        onClick={() => onChange([])}
        type="button"
      >
        Tout le corpus
      </button>
    </div>
  );
}
